// Copyright 2019 Sorama B.V.

import { IWorkflowModuleCatalog } from "catalogs/IWorkflowModuleCatalog";
import { GenericMeasurement, MeasurementPartType } from "datatypes/SoramaWebApi";
import {
    IWorkflowModuleConfig,
    WorkflowModuleType,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModuleConfig";
import { IMeasurementProcessorResolver } from "services/Measurement/IMeasurementProcessorResolver";
import { IBeamformingProcessingService } from "services/SoundProcessing/IBeamformingProcessingService";
import { ISpectralProcessingService } from "services/SoundProcessing/ISpectralProcessingService";

/**
 * Helper class to determine the available analysis modules for a measurement
 */
export class MeasurementAnalysisSet {
    constructor(
        private readonly moduleCatalog: IWorkflowModuleCatalog,
        private readonly processorResolver: IMeasurementProcessorResolver
    ) {}

    public getAvailableAnalysisTypes(measurement: GenericMeasurement): Promise<IWorkflowModuleConfig[]> {
        const promises: Promise<IWorkflowModuleConfig[]>[] = [
            this.getAvailableFarFieldModules(measurement),
            this.getAvailableNearFieldModules(measurement),
            this.getAvailableGenericModules(measurement),
        ];

        return Promise.all(promises).then((x) => {
            return [].concat(...x);
        });
    }

    private getAvailableFarFieldModules(measurement: GenericMeasurement): Promise<IWorkflowModuleConfig[]> {
        if (
            measurement.isComputedMeasurement ||
            measurement.parts.filter((x) => x.measurementType == MeasurementPartType.Audio).length > 1
        ) {
            // Scanning measurements and computed measurements are not supported at the moment
            return Promise.resolve([]);
        }

        const farfieldModules = this.moduleCatalog.getWorkflowModules(WorkflowModuleType.FarFieldAnalysis);

        return this.processorResolver
            .canProcess([measurement], IBeamformingProcessingService)
            .then((x) => {
                if (x[0].available) return farfieldModules;

                return [];
            })
            .catch(() => []);
    }

    private getAvailableNearFieldModules(measurement: GenericMeasurement): Promise<IWorkflowModuleConfig[]> {
        if (
            !measurement.uraProperties ||
            measurement.uraProperties.sensorHeight < 8 ||
            measurement.uraProperties.sensorWidth < 8
        ) {
            // Should be URA and at least 8x8 mics
            return Promise.resolve([]);
        }

        // Holography is always available if the data is available
        return Promise.resolve(this.moduleCatalog.getWorkflowModules(WorkflowModuleType.NearFieldAnalysis));
    }

    private getAvailableGenericModules(measurement: GenericMeasurement): Promise<IWorkflowModuleConfig[]> {
        return this.processorResolver
            .canProcess([measurement], ISpectralProcessingService)
            .then((x) => {
                if (x[0].available) return this.moduleCatalog.getWorkflowModules(WorkflowModuleType.GenericAnalysis);

                return [];
            })
            .catch(() => []);
    }
}
